import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'

import { PageCard, PageLayout } from '@/components/ui/page'
import {
	CellText,
	CellTextDate,
	DataTable,
	useDataTable,
} from '@/components/ui/table/data-table'

import { useAuth } from '@/libs/hooks/use-auth'
import { toIdr } from '@/libs/utils/format'

import { productList } from '@/features/product/api'
import type { IProduct } from '@/features/product/type'

export const Route = createFileRoute('/_app/product/')({
	component: Component,
})

function Component() {
	return (
		<PageLayout.Root>
			<PageLayout.Header breadcrumbs={[['/product', 'Produk']]} />
			<PageCard.Root>
				<PageCard.Header
					title="Daftar Produk"
					actions={
						<PageCard.HeaderActionRoot>
							<PageCard.HeaderAction asChild>
								<Link from={Route.fullPath} to="/product/create">
									Buat produk
								</Link>
							</PageCard.HeaderAction>
						</PageCard.HeaderActionRoot>
					}
				/>
				<PageCard.Content>
					<ProductTable />
				</PageCard.Content>
			</PageCard.Root>
		</PageLayout.Root>
	)
}

function ProductTable() {
	const { outletId } = useAuth((s) => s.outlet)
	const { isLoading, data } = useQuery(productList.qry({ outletId }))
	const navigate = Route.useNavigate()

	const columns = useMemo(() => getColumns(), [])
	const { table } = useDataTable({
		columns,
		data: data?.products ?? [],
	})

	return (
		<DataTable
			table={table}
			columns={columns}
			isLoading={isLoading}
			onRowClick={(row) => navigate({ to: `/product/${row.original.id}` })}
		/>
	)
}

const ch = createColumnHelper<IProduct>()

function getColumns() {
	return [
		ch.accessor('name', {
			header: 'Nama',
			cell: ({ row }) => <CellText text={row.original.name} />,
		}),
		ch.accessor('variants', {
			id: 'variants',
			header: 'Varian',
			cell: ({ row }) => {
				const { hasVariant, variants } = row.original
				const text = !hasVariant ? '-' : variants.map((x) => x.name).join(', ')
				return <CellText text={text} />
			},
		}),
		ch.accessor('price', {
			header: 'Harga Jual',
			cell: ({ row }) => <CellText text={toIdr(row.original.price)} />,
		}),
		ch.accessor('createdAt', {
			header: 'Created At',
			cell: ({ row }) => <CellTextDate date={row.original.createdAt} />,
		}),
		ch.accessor('updatedAt', {
			header: 'Updated At',
			cell: ({ row }) => <CellTextDate date={row.original.updatedAt} />,
		}),
	]
}
